import _ from 'lodash';
import {
  colors,
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from '@material-ui/core';
import { esES as esLocale } from '@material-ui/core/locale';

import { THEMES } from 'constants/index';

import typography from './typography';
import PINPEO, { STATUS_PILL } from './palette';
import { resolveBrand } from './brand';
import { softShadows, strongShadows } from './shadows';

const baseConfig = {
  direction: 'ltr',
  typography,
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'unset !important',
        borderRadius: PINPEO.radius,
        fontWeight: 600,
      },
      outlined: {
        borderColor: PINPEO.borderStrong,
      },
    },
    MuiChip: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.075)',
        borderRadius: 99,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: PINPEO.radiusCard,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: PINPEO.radiusCard,
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: PINPEO.radius,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: PINPEO.borderStrong,
        },
      },
    },
    MUIDataTable: {
      paper: {
        border: `1px solid ${PINPEO.border}`,
        borderRadius: PINPEO.radiusCard,
        boxShadow: 'none',
      },
    },
    MUIDataTableBodyRow: {
      root: {
        '&:nth-child(even)': {
          backgroundColor: 'transparent',
        },
        '&:hover': {
          backgroundColor: `${PINPEO.rowHover} !important`,
        },
      },
    },
    // Toolbar de mui-datatables: iconos como botones pill (§2.3).
    // Los estados hover/activo (color de marca) se agregan por-tenant en
    // createTheme (brandConfig): aquí solo va lo neutro.
    MUIDataTableToolbar: {
      root: {
        paddingLeft: 16,
        paddingRight: 12,
      },
      icon: {
        border: `1px solid ${PINPEO.borderStrong}`,
        borderRadius: 9,
        backgroundColor: PINPEO.surface,
        color: PINPEO.textSecondary,
        padding: 7,
        marginLeft: 6,
      },
    },
    MUIDataTableSearch: {
      main: {
        border: `1px solid ${PINPEO.borderStrong}`,
        borderRadius: PINPEO.radius,
        backgroundColor: PINPEO.surface,
        padding: '2px 10px',
        marginRight: 8,
      },
      searchIcon: {
        color: PINPEO.textMuted,
        marginTop: 0,
        marginRight: 6,
      },
      clearIcon: {
        color: PINPEO.textMuted,
      },
    },
    MUIDataTableFilterList: {
      chip: {
        fontWeight: 600,
        borderRadius: 99,
      },
    },
    MUIDataTablePagination: {
      root: {
        borderTop: `1px solid ${PINPEO.border}`,
        color: PINPEO.textSecondary,
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32,
      },
    },
    MuiTab: {
      root: {
        textTransform: 'unset !important',
      },
    },
    MuiTableRow: {
      root: {
        '&$selected': {
          backgroundColor: PINPEO.rowSelected,
        },
      },
      hover: {
        '&:hover': {
          backgroundColor: PINPEO.rowHover,
        },
      },
    },
  },
};

export function createTheme(settings = {}) {
  // White-label (guía §5): `settings` es el objeto `theme` del JSON del tenant
  // ({ colors: { primary, secondary, sidebar? } }). Los tokens se resuelven en
  // runtime con fallbacks neutros de prod (#012B55 / #002B55) — ante un error
  // de config ningún tenant ve la marca de otro cliente.
  const brand = resolveBrand(settings?.colors);

  // Overrides que dependen del color de marca del tenant: no pueden vivir en
  // baseConfig (se evalúan por-tenant en cada carga).
  const brandConfig = {
    palette: {
      sidebar: {
        main: brand.sidebar,
      },
    },
    overrides: {
      MUIDataTableToolbar: {
        icon: {
          '&:hover': {
            backgroundColor: brand.primarySoft,
            borderColor: brand.primary,
            color: brand.primary,
          },
        },
        iconActive: {
          backgroundColor: brand.primarySoft,
          borderColor: brand.primary,
          color: brand.primary,
        },
      },
      MUIDataTableFilterList: {
        chip: {
          backgroundColor: brand.primarySoft,
          color: brand.primary,
        },
      },
    },
  };

  const themeConfigs = [
    {
      name: THEMES.LIGHT,
      overrides: {
        MuiTableCell: {
          root: {
            borderBottom: `1px solid ${PINPEO.divider}`,
          },
          // Fiel a prod (guía §5): la cabecera de tabla consume el secondary
          // del tenant (con fallback neutro), texto blanco.
          head: {
            padding: '6px !important',
            backgroundColor: `${brand.secondary} !important`,
            color: `${colors.common.white} !important`,
            '& .MuiButton': {
              color: `${colors.common.white} !important`,
            },
          },
        },
        MuiInputBase: {
          input: {
            '&::placeholder': {
              opacity: 1,
              color: colors.blueGrey[600],
            },
          },
        },
        MUIDataTableHeadCell: {
          contentWrapper: {
            width: '100% !important',
            display: 'block',
            alignItems: 'center',
          },
          dragCursor: {
            width: '100%',
          },
          sortAction: {
            alignItems: 'center',
            backgroundColor: 'aqua',
            cursor: 'pointer',
            display: 'contents',
            justifyContent: 'left',
            width: '100% !important',
          },
          toolButton: {
            width: '100%',
            marginLeft: '0',
            marginRight: '0',
          },
        },
        MuiButton: {
          label: {
            '& span': {
              textAlign: 'left !important',
            },
          },
        },
      },
      palette: {
        type: 'light',
        action: {
          active: PINPEO.textSecondary,
          hover: PINPEO.rowHover,
        },
        background: {
          default: PINPEO.background,
          dark: PINPEO.background,
          paper: PINPEO.surface,
        },
        divider: PINPEO.border,
        primary: {
          main: brand.primary,
          dark: brand.primaryHover,
        },
        secondary: {
          main: brand.secondary,
        },
        success: {
          main: STATUS_PILL.success.fg,
          light: STATUS_PILL.success.bg,
        },
        warning: {
          main: STATUS_PILL.warning.fg,
          light: STATUS_PILL.warning.bg,
        },
        error: {
          main: STATUS_PILL.error.fg,
          light: STATUS_PILL.error.bg,
        },
        info: {
          main: STATUS_PILL.info.fg,
          light: STATUS_PILL.info.bg,
        },
        text: {
          primary: PINPEO.text,
          secondary: PINPEO.textSecondary,
        },
      },
      shadows: softShadows,
    },
    {
      name: THEMES.ONE_DARK,
      palette: {
        type: 'dark',
        action: {
          active: 'rgba(255, 255, 255, 0.54)',
          hover: 'rgba(255, 255, 255, 0.04)',
          selected: 'rgba(255, 255, 255, 0.08)',
          disabled: 'rgba(255, 255, 255, 0.26)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
          focus: 'rgba(255, 255, 255, 0.12)',
        },
        background: {
          default: '#282C34',
          dark: '#1c2025',
          paper: '#282C34',
        },
        primary: {
          main: '#8a85ff',
        },
        secondary: {
          main: '#8a85ff',
        },
        text: {
          primary: '#e6e5e8',
          secondary: '#adb0bb',
        },
      },
      shadows: strongShadows,
    },
    {
      name: THEMES.UNICORN,
      palette: {
        type: 'dark',
        action: {
          active: 'rgba(255, 255, 255, 0.54)',
          hover: 'rgba(255, 255, 255, 0.04)',
          selected: 'rgba(255, 255, 255, 0.08)',
          disabled: 'rgba(255, 255, 255, 0.26)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
          focus: 'rgba(255, 255, 255, 0.12)',
        },
        background: {
          default: '#2a2d3d',
          dark: '#222431',
          paper: '#2a2d3d',
        },
        primary: {
          main: '#a67dff',
        },
        secondary: {
          main: '#a67dff',
        },
        text: {
          primary: '#f6f5f8',
          secondary: '#9699a4',
        },
      },
      shadows: strongShadows,
    },
  ];

  let themeConfig = themeConfigs.find((theme) => theme.name === settings.theme);

  if (!themeConfig) {
    [themeConfig] = themeConfigs;
  }

  let theme = createMuiTheme(
    _.merge(
      {},
      baseConfig,
      brandConfig,
      themeConfig,
      {
        direction: settings.direction,
      },
    ),
    esLocale,
  );

  if (settings.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
}
